import {i18n} from '../../i18n/i18n'

import React, {Component} from 'react'
import {RouteComponentProps, withRouter} from 'react-router-dom'
import {connect, ConnectedProps} from 'react-redux'
import routes from '../../routes/routes'
import {RootState} from '../../store'
import {replaceLimit, replacePage, replaceSearch, replaceLabelableType, replaceSorting} from './SystemLabels.slice'
import {paginate, deleteCustomLabel} from './SystemLabels.thunk'
import {ConfirmParams} from '../../components/Confirm/Confirm.state'
import confirmService from '../../services/confirm.service'
import {TableHorizontalHeader} from '../../components/TableHorizontal'
import {ActionName} from '../../components/Actions'
import Button from '../../components/Button'
import Card from '../../components/Card'
import ContentLayout from '../../components/ContentLayout'
import ContentMain from '../../components/ContentMain'
import HeadingRow from '../../components/HeadingRow'
import Gallery from '../../components/Gallery'
import storageService from '../../services/storage.service'
import {RadioGroupItem} from '../../components/RadioGroup'
import customLabelApi, {
    ApiCalculatedField,
    ApiCalculationOperand,
    ApiConditionOperator,
    ApiFieldCondition,
    ApiFieldConditionalRule
} from '../../apis/customLabel.api'
import ImagePopup from '../../components/Popup/image_popup'
import labelApprovalApi, {
    LabelApprovalData,
    LabelApprovalLabelableType
} from '../../apis/labelApproval.api'
import LabelApprovalRequestForm from '../../partials/LabelApprovalRequestForm'
import LabelApprovalHistory from '../../partials/LabelApprovalHistory'
import LabelVersionHistory from '../../partials/LabelVersionHistory'
import CustomLabelCreateForm from '../../partials/CustomLabelCreateForm'
import CustomLabelEditForm from '../../partials/CustomLabelEditForm'
import {useUserPlan} from "../../utils/userPlan.util";
import InputText from "../../components/InputText";
import Modal from "../../components/Modal";
import alertService from "../../services/alert.service";
import Icon from "../../components/Icon";
import Tooltip from "../../components/Tooltip/Tooltip";

interface CustomFieldRow {
    name: string
    sample: string
    isPii: boolean
    calculated: ApiCalculatedField | null
}

interface Props
    extends RouteComponentProps, ConnectedProps<typeof connector> {
}

interface State {
    isCustomLabel: boolean
    isPopUpOpen: boolean
    selectedImage: string
    previewId: number | null
    previewName: string
    previewApproval: LabelApprovalData | null
    previewCanApprove: boolean
    previewStage: {width: number, height: number, unit: string} | null
    isResending: boolean
    versionsForId: number | null
    versionsName: string
    isApprovalModalOpen: boolean
    isApprovalHistoryOpen: boolean
    approvalHistory: LabelApprovalData[]
    approvalHistoryName: string
    isCreateModalOpen: boolean
    isEditId: number | null
    isCustomFieldModalOpen: boolean
    customFields: CustomFieldRow[]
    selectedApiMethod: ApiMethod
    isExampleVisible: boolean
    editingCalculatedFieldIndex: number | null
}

type ApiMethod =
    | 'fetch_markups'
    | 'print_markups'
    | 'printNodeZpl'
    | 'printNodePdf'
    | 'export_svg'

class SystemLabels extends Component<Props, State> {
    userPlan = useUserPlan();

    state: State = {
        isCustomLabel: false,
        isPopUpOpen: false,
        selectedImage: '',
        previewId: null,
        previewName: '',
        previewApproval: null,
        previewCanApprove: false,
        previewStage: null,
        isResending: false,
        versionsForId: null,
        versionsName: '',
        isApprovalModalOpen: false,
        isApprovalHistoryOpen: false,
        approvalHistory: [],
        approvalHistoryName: '',
        isCreateModalOpen: false,
        isEditId: null,
        isCustomFieldModalOpen: false,
        customFields: [{name: 'label_name', sample: 'Label A', isPii: false, calculated: null}],
        selectedApiMethod: 'fetch_markups',
        isExampleVisible: false,
        editingCalculatedFieldIndex: null
    }

    allSearchOptions: RadioGroupItem[] = [
        {name: 'customLabel', label: 'Custom label'},
        {name: 'site', label: 'Global'},
        {name: 'contact', label: 'Contact'},
        {name: 'item', label: 'Item'},
        {name: 'category', label: 'Category'}
    ]

    searchOptions: RadioGroupItem[] = this.userPlan.isLabelBuilderUser
        ? this.allSearchOptions.filter(option => option.name === 'customLabel')
        : this.allSearchOptions


    defaultRadioOption = 'customLabel'

    constructor(props: Props) {
        super(props)
        this.handleLimitChange = this.handleLimitChange.bind(this)
        this.handleSearch = this.handleSearch.bind(this)
        this.handlePaginate = this.handlePaginate.bind(this)
        this.handleCreateClick = this.handleCreateClick.bind(this)
        this.handleActionClick = this.handleActionClick.bind(this)
        this.handleRadioChange = this.handleRadioChange.bind(this)
        this.handleSorting = this.handleSorting.bind(this)
        this.handleImageClick = this.handleImageClick.bind(this)
        this.handlePopupClose = this.handlePopupClose.bind(this)
    }

    headers: TableHorizontalHeader[] = [
        {heading: i18n('Name'), path: 'name'},
        {heading: i18n('Item'), path: 'item.code'},
        {heading: i18n('CSV'), downloadText: 'csvFilename', path: 'downloadUrls.csv'},
        {heading: i18n('API'), downloadText: 'apiUrl', path: 'downloadUrls.api'},
        {
            heading: i18n('Created at'),
            path: 'createdAt',
            date: true
        },
        {
            heading: i18n('Updated at'),
            path: 'updatedAt',
            date: true
        },
        {heading: i18n('Actions')},
        {heading: i18n('Snapshot'), path: 'snapshot'}
    ]

    closeCreateModal = () => {
        this.setState({isCreateModalOpen: false, isEditId: null})
    }

    handleCreateClick() {
        const {isCustomLabel} = this.state

        if (isCustomLabel) {
            return this.setState({
                isCreateModalOpen: true,
                isEditId: null
            })
        }

        let currentLabelTarget = storageService.get('systemLabelSearch')
        if (currentLabelTarget == '') {
            currentLabelTarget = this.defaultRadioOption
        }
        const pathname = this.state.isCustomLabel ? routes.systemLabelCreate : routes.labelBuilder
        const labelKey = this.state.isCustomLabel
            ? 'customLabelId'
            : 'systemLabelId'

        const state = {labelTarget: currentLabelTarget, [labelKey]: null}
        this.props.history.push({pathname, state})

    }

    handleAddCustomField = () => {
        this.setState(prev => {
            if (prev.customFields.length >= 30) return null

            return {
                customFields: [
                    ...prev.customFields,
                    { name: '', sample: '', isPii: false, calculated: null }
                ]
            }
        })
    }

    handleCustomFieldChange = (
        index: number,
        field: 'name' | 'sample' | 'isPii',
        value: string | boolean
    ) => {
        this.setState(prev => {
            const fields = [...prev.customFields]
            const oldName = fields[index].name

            fields[index] = {
                ...fields[index],
                [field]: value
            }

            if (field === 'name' && typeof value === 'string' && oldName !== value) {
                fields.forEach((row, rowIndex) => {
                    if (!row.calculated) return
                    const calculated = JSON.parse(JSON.stringify(row.calculated)) as ApiCalculatedField
                    if (rowIndex === index || calculated.target_field === oldName) {
                        calculated.target_field = value
                    }
                    calculated.rules?.forEach(rule => {
                        rule.conditions.forEach(condition => {
                            if (condition.field === oldName) condition.field = value
                        })
                    })
                    calculated.calculation?.operands.forEach(operand => {
                        if (operand.type === 'field' && operand.value === oldName) {
                            operand.value = value
                        }
                    })
                    fields[rowIndex] = {...row, calculated}
                })
            }

            return { customFields: fields }
        })
    }

    handleCustomFieldDelete = (index: number) => {
        this.setState(prev => {
            const fields = [...prev.customFields]
            const removedName = fields[index].name
            fields.splice(index, 1)

            fields.forEach((row, rowIndex) => {
                if (!row.calculated) return
                const calculated = JSON.parse(JSON.stringify(row.calculated)) as ApiCalculatedField
                if (calculated.type === 'calculation' && calculated.calculation?.operands.some(
                    operand => operand.type === 'field' && operand.value === removedName
                )) {
                    fields[rowIndex] = {...row, calculated: null}
                    return
                }
                if (calculated.rules) {
                    calculated.rules = calculated.rules
                        .map(rule => ({
                            ...rule,
                            conditions: rule.conditions.filter(condition => condition.field !== removedName)
                        }))
                        .filter(rule => rule.conditions.length > 0)
                    if (calculated.rules.length === 0) {
                        fields[rowIndex] = {...row, calculated: null}
                        return
                    }
                }
                fields[rowIndex] = {...row, calculated}
            })

            return {customFields: fields, editingCalculatedFieldIndex: null}
        })
    }

    closeCustomFieldModal = () => {
        this.setState({
            isCustomFieldModalOpen: false,
            customFields: [{name: 'label_name', sample: 'Label A', isPii: false, calculated: null}],
            selectedApiMethod: 'fetch_markups',
            isExampleVisible: false,
            editingCalculatedFieldIndex: null
        })
    }

    handleSaveCustomFields = () => {
        if (!this.state.isEditId) return

        const filteredFields = this.state.customFields
            .filter(field => field.name && field.name.trim() !== '')

        const normalizedNames = filteredFields.map(field => field.name.trim())
        if (new Set(normalizedNames).size !== normalizedNames.length) {
            alertService.error('Custom field names must be unique')
            return
        }

        const calculatedError = this.getCalculatedFieldValidationError(filteredFields)
        if (calculatedError) {
            alertService.error(calculatedError)
            return
        }

        if (filteredFields.length === 0) {
            alertService.error(i18n('Please add at least one custom field'))
            return
        }

        const payload: any = {}
        let piiIndex = 0

        filteredFields.forEach((field, index) => {
            payload[`api_field[${index}]`] = field.name.trim()
            payload[`sample_data[${index}]`] = field.sample ?? ''

            if (field.isPii) {
                payload[`pii_fields[${piiIndex}]`] = field.name.trim()
                piiIndex += 1
            }
        })
        payload.conditional_rules = JSON.stringify(
            filteredFields
                .filter(field => field.calculated)
                .map(field => ({...field.calculated!, target_field: field.name.trim()}))
        )

        customLabelApi.customField(
            this.state.isEditId,
            payload,
            (customLabel) => {
                if (!customLabel) return

                alertService.success(i18n('Successfully created custom fields'))
                this.closeCustomFieldModal()
                this.props.paginate()
            }
        )
    }

    getCalculatedFieldValidationError = (fields: CustomFieldRow[]): string | null => {
        const names = new Set(fields.map(field => field.name.trim()))

        for (const field of fields) {
            const calculated = field.calculated
            if (!calculated) continue

            if (field.name.trim() === 'label_name') {
                return 'label_name is reserved and cannot be calculated'
            }

            if (calculated.type === 'conditional') {
                if (!calculated.rules?.length) {
                    return `${field.name} must have at least one IF rule`
                }
                for (const rule of calculated.rules) {
                    if (!rule.conditions.length) {
                        return `${field.name} has an IF rule without a condition`
                    }
                    if (rule.conditions.some(condition => !names.has(condition.field))) {
                        return `${field.name} references a field that no longer exists`
                    }
                }
            }

            if (calculated.type === 'calculation') {
                if (!calculated.calculation) {
                    return `${field.name} must have a calculation`
                }
                for (const operand of calculated.calculation.operands) {
                    if (operand.type === 'field' && !names.has(String(operand.value))) {
                        return `${field.name} references a field that no longer exists`
                    }
                    if (operand.type === 'number' && (
                        String(operand.value).trim() === '' || Number.isNaN(Number(operand.value))
                    )) {
                        return `${field.name} has an invalid numeric operand`
                    }
                }
            }

            if (
                calculated.fallback?.type === 'literal'
                && String(calculated.fallback.value ?? '').trim() === ''
            ) {
                return `${field.name} requires a fixed fallback value`
            }
        }

        return null
    }

    handleGetCustomField = (id: number) => {
        customLabelApi.getCustomField(id, (response) => {

            let fields: CustomFieldRow[] = [{
                name: 'label_name',
                sample: 'Label A',
                isPii: false,
                calculated: null
            }]

            if (response?.attributes?.api_field) {
                try {
                    const names = this.parseCustomFieldArray(response.attributes.api_field)
                    const samples = this.parseCustomFieldArray(response.attributes.sample_data)
                    const piiFields = new Set(
                        this.parseCustomFieldArray(response.attributes.pii_fields)
                    )
                    const calculatedRules = this.parseCustomFieldArray<ApiCalculatedField>(
                        response.attributes.conditional_rules
                    )
                    const calculatedByTarget = new Map(
                        calculatedRules.map(rule => [rule.target_field, rule])
                    )

                    fields = names.map((name: string, index: number) => ({
                        name,
                        sample: samples[index] ?? '',
                        isPii: piiFields.has(name),
                        calculated: calculatedByTarget.get(name) ?? null
                    }))

                } catch (error) {
                    console.error("Error parsing api fields:", error)
                }
            }

            this.setState({
                isCustomFieldModalOpen: true,
                isEditId: id,
                customFields: fields,
                selectedApiMethod: 'fetch_markups',
                isExampleVisible: false,
                editingCalculatedFieldIndex: null
            })
        })
    }

    handleMethodChange = (selectedApiMethod: ApiMethod) => {
        this.setState({selectedApiMethod})
    }

    parseCustomFieldArray = <T = string,>(value?: T[] | string): T[] => {
        if (!value) return []
        if (Array.isArray(value)) return value

        try {
            const parsed = JSON.parse(value)
            return Array.isArray(parsed) ? parsed : []
        } catch (error) {
            console.error('Error parsing custom field array:', error)
            return []
        }
    }

    get availableCalculatedFields() {
        return this.state.customFields
            .map(field => field.name.trim())
            .filter(name => name !== '')
    }

    createDefaultCalculatedField = (targetField: string): ApiCalculatedField => ({
        target_field: targetField,
        type: 'conditional',
        rules: [{
            combinator: 'and',
            conditions: [{
                field: targetField,
                operator: 'equals',
                value: ''
            }],
            result: {type: 'literal', value: ''}
        }],
        fallback: {type: 'original'}
    })

    handleCalculatedToggle = (index: number, enabled: boolean) => {
        if (index === 0) return
        if (!enabled && !window.confirm('Remove the calculated-field configuration for this field?')) {
            return
        }

        this.setState(prev => {
            const customFields = [...prev.customFields]
            const row = customFields[index]
            customFields[index] = {
                ...row,
                calculated: enabled
                    ? (row.calculated ?? this.createDefaultCalculatedField(row.name.trim()))
                    : null
            }
            return {
                customFields,
                editingCalculatedFieldIndex: enabled ? index : null
            }
        })
    }

    updateCalculatedField = (
        updater: (calculated: ApiCalculatedField) => ApiCalculatedField
    ) => {
        this.setState(prev => {
            const index = prev.editingCalculatedFieldIndex
            if (index === null) return null
            const customFields = [...prev.customFields]
            const current = customFields[index].calculated
                ?? this.createDefaultCalculatedField(customFields[index].name.trim())
            customFields[index] = {
                ...customFields[index],
                calculated: updater(JSON.parse(JSON.stringify(current)))
            }
            return {customFields}
        })
    }

    handleCalculatedEditorDone = () => {
        const index = this.state.editingCalculatedFieldIndex
        const calculated = index === null ? null : this.state.customFields[index]?.calculated
        if (
            calculated?.fallback?.type === 'literal'
            && String(calculated.fallback.value ?? '').trim() === ''
        ) {
            alertService.error('Enter a fixed fallback value before continuing')
            return
        }
        this.setState({editingCalculatedFieldIndex: null})
    }

    handleCalculatedTypeChange = (type: 'conditional' | 'calculation') => {
        this.updateCalculatedField(calculated => {
            if (type === 'conditional') {
                return {
                    target_field: calculated.target_field,
                    type,
                    rules: calculated.rules?.length ? calculated.rules : [{
                        combinator: 'and',
                        conditions: [{
                            field: calculated.target_field,
                            operator: 'equals',
                            value: ''
                        }],
                        result: {type: 'literal', value: ''}
                    }],
                    fallback: calculated.fallback ?? {type: 'original'}
                }
            }

            return {
                target_field: calculated.target_field,
                type,
                calculation: calculated.calculation ?? {
                    operator: 'multiply',
                    operands: [
                        {type: 'field', value: calculated.target_field},
                        {type: 'number', value: 1}
                    ]
                },
                fallback: calculated.fallback ?? {type: 'original'}
            }
        })
    }

    handleAddConditionalRule = () => {
        this.updateCalculatedField(calculated => ({
            ...calculated,
            rules: [
                ...(calculated.rules ?? []),
                {
                    combinator: 'and',
                    conditions: [{
                        field: calculated.target_field,
                        operator: 'equals',
                        value: ''
                    }],
                    result: {type: 'literal', value: ''}
                }
            ]
        }))
    }

    handleRemoveConditionalRule = (ruleIndex: number) => {
        this.updateCalculatedField(calculated => ({
            ...calculated,
            rules: (calculated.rules ?? []).filter((_, index) => index !== ruleIndex)
        }))
    }

    handleConditionalRuleChange = (
        ruleIndex: number,
        updater: (rule: ApiFieldConditionalRule) => ApiFieldConditionalRule
    ) => {
        this.updateCalculatedField(calculated => ({
            ...calculated,
            rules: (calculated.rules ?? []).map((rule, index) =>
                index === ruleIndex ? updater(rule) : rule
            )
        }))
    }

    handleAddCondition = (ruleIndex: number) => {
        this.handleConditionalRuleChange(ruleIndex, rule => ({
            ...rule,
            conditions: [
                ...rule.conditions,
                {field: this.availableCalculatedFields[0] ?? '', operator: 'equals', value: ''}
            ]
        }))
    }

    handleConditionChange = (
        ruleIndex: number,
        conditionIndex: number,
        changes: Partial<ApiFieldCondition>
    ) => {
        this.handleConditionalRuleChange(ruleIndex, rule => ({
            ...rule,
            conditions: rule.conditions.map((condition, index) =>
                index === conditionIndex ? {...condition, ...changes} : condition
            )
        }))
    }

    handleRemoveCondition = (ruleIndex: number, conditionIndex: number) => {
        this.handleConditionalRuleChange(ruleIndex, rule => ({
            ...rule,
            conditions: rule.conditions.filter((_, index) => index !== conditionIndex)
        }))
    }

    conditionNeedsValue = (operator: ApiConditionOperator) => ![
        'is_empty',
        'is_not_empty',
        'is_true',
        'is_false'
    ].includes(operator)

    get conditionOperatorOptions(): Array<{value: ApiConditionOperator, label: string}> {
        return [
            {value: 'equals', label: 'Equals'},
            {value: 'not_equals', label: 'Does not equal'},
            {value: 'contains', label: 'Contains'},
            {value: 'not_contains', label: 'Does not contain'},
            {value: 'starts_with', label: 'Starts with'},
            {value: 'ends_with', label: 'Ends with'},
            {value: 'greater_than', label: 'Greater than'},
            {value: 'greater_than_or_equal', label: 'Greater than or equal'},
            {value: 'less_than', label: 'Less than'},
            {value: 'less_than_or_equal', label: 'Less than or equal'},
            {value: 'is_empty', label: 'Is empty'},
            {value: 'is_not_empty', label: 'Is not empty'},
            {value: 'is_true', label: 'Is true'},
            {value: 'is_false', label: 'Is false'}
        ]
    }

    handleExampleVisibility = () => {
        this.setState(prev => ({isExampleVisible: !prev.isExampleVisible}))
    }

    handleCopyExample = async () => {
        try {
            await navigator.clipboard.writeText(this.exampleRequestText)
            alertService.success('Example copied to clipboard')
        } catch (error) {
            console.error(error)
            alertService.error('Unable to copy example')
        }
    }

    get filteredCustomFields() {
        return this.state.customFields
            .filter(field => field.name && field.name.trim() !== '')
            .map(field => ({
                name: field.name.trim(),
                sample: field.sample ?? '',
                isPii: field.isPii ?? false
            }))
    }

    get sampleApiData() {
        return this.filteredCustomFields.reduce<Record<string, string>>((acc, field) => {
            acc[field.name] = field.sample
            return acc
        }, {})
    }

    get sampleLabelName() {
        return this.sampleApiData.label_name || 'Label A'
    }

    get exampleApiBaseUrl() {
        return `${window.location.origin}/api`
    }

    get methodOptions(): {value: ApiMethod, label: string}[] {
        return [
            {value: 'fetch_markups', label: 'Fetch Markups'},
            {value: 'print_markups', label: 'Print Markups'},
            {value: 'printNodeZpl', label: 'PrintNode ZPL'},
            {value: 'printNodePdf', label: 'PrintNode PDF'},
            {value: 'export_svg', label: 'Export SVG'}
        ]
    }

    get requestBodyObject() {
        const body: Record<string, any> = {
            label_name: this.sampleLabelName,
            amount: 1,
            apiData: this.sampleApiData
        }

        if (this.state.selectedApiMethod === 'printNodeZpl' || this.state.selectedApiMethod === 'printNodePdf') {
            body.printer_id = 75236019
        }

        return body
    }

    get selectedMethodMeta() {
        const {selectedApiMethod} = this.state

        const bodyJson = JSON.stringify(this.requestBodyObject, null, 2)
        const sdkPayload = JSON.stringify({
            label_name: this.sampleLabelName,
            amount: 1,
            apiData: this.sampleApiData,
            mode: selectedApiMethod,
            ...((selectedApiMethod === 'printNodeZpl' || selectedApiMethod === 'printNodePdf')
                ? {printer_id: 75236019}
                : {})
        }, null, 2)

        const directFetch = (endpoint: string) => `const response = await fetch("${this.exampleApiBaseUrl}${endpoint}", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    "X-API-KEY": "YOUR_API_KEY",
    "X-API-SECRET": "YOUR_API_TOKEN"
  },
  body: JSON.stringify(${bodyJson})
});

const result = await response.json();
console.log(result);`

        const sdkExample = `<!-- Add this only when SDK is needed -->
<script src="https://app.quandosol.com/qcim-label-sdk.js"></script>
<script>
  QCIMLabelSDK.setConfig({
    apiBaseUrl: "${this.exampleApiBaseUrl}",
    api_key: "YOUR_API_KEY",
    api_token: "YOUR_API_TOKEN",
    printPageUrl: "./print-label.html",
    debug: true
  });

  QCIMLabelSDK.printLabel(${sdkPayload});
</script>`

        if (selectedApiMethod === 'fetch_markups') {
            return {
                title: 'Fetch Markups',
                note: 'Direct API call. No SDK required.',
                code: directFetch('/custom-labels/fetch-markups')
            }
        }

        if (selectedApiMethod === 'print_markups') {
            return {
                title: 'Print Markups',
                note: 'Renderer SDK required. Customer must add the hosted SDK and a print-label.html page.',
                code: sdkExample
            }
        }

        if (selectedApiMethod === 'printNodeZpl') {
            return {
                title: 'PrintNode ZPL',
                note: 'Direct API call. No SDK required. Replace printer_id with the customer printer id.',
                code: directFetch('/custom-labels/print-node')
            }
        }

        if (selectedApiMethod === 'printNodePdf') {
            return {
                title: 'PrintNode PDF',
                note: 'Direct API call. No SDK required. Replace printer_id with the customer printer id.',
                code: directFetch('/custom-labels/print-node-pdf')
            }
        }

        return {
            title: 'Export SVG',
            note: 'Renderer SDK required. No print-label.html file is needed for this method.',
            code: sdkExample
        }
    }

    get exampleRequestText() {
        const {title, note, code} = this.selectedMethodMeta
        return `${title}\n\n${note}\n\n${code}`
    }


    handleRadioChange(name: string) {
        storageService.set('systemLabelSearch', name)
        this.props.replaceLabelableType(name)

        const isCustomLabel = name === 'customLabel'
        console.log('isCustomLabel', isCustomLabel)

        this.setState({isCustomLabel})
    }

    handleLimitChange = (limit: string) => {
        storageService.set('customLabelLimit', limit)
        this.props.replaceLimit(Number(limit))
    }

    handleSearch(search: string) {
        storageService.set('customLabelSearch', search)
        this.props.replaceSearch(search)
    }

    handlePaginate(page: number) {
        storageService.set('customLabelPage', page)
        this.props.replacePage(page)
    }

    handleSorting(sortBy: string) {
        console.log('sortBy: ' + sortBy)
        storageService.set('customLabelSorting', sortBy)
        this.props.replaceSorting(sortBy)
    }

    handleImageClick(clickedImage: string) {
        this.setState({isPopUpOpen: true, selectedImage: clickedImage})
    }

    handlePopupClose() {
        this.setState({
            isPopUpOpen: false,
            selectedImage: '',
            previewId: null,
            previewName: '',
            previewApproval: null,
            previewCanApprove: false,
            previewStage: null
        })
    }

    /**
     * Which id space the current gallery mode uses — custom labels are keyed by
     * `customLabels.id`, every other type by `labels.id`.
     */
    get labelableType(): LabelApprovalLabelableType {
        return this.state.isCustomLabel ? 'customLabel' : 'systemLabel'
    }

    /**
     * Clicking the card image opens the same preview modal as the preview
     * action, so the approval button is reachable either way. The snapshot is
     * already on the row, so no preview call is needed here.
     */
    handleImagePreview = (id: number, image: string) => {
        const row = this.props.data.find((label: {id: number}) => label.id === id)
        this.setState({
            previewId: id,
            previewName: row?.name ?? '',
            previewApproval: row?.approval ?? null,
            // An empty snapshot means the row has no saved design, so there is
            // nothing that could be sent for approval.
            previewCanApprove: Boolean(row?.snapshot),
            isPopUpOpen: true,
            selectedImage: image
        })
    }

    handleApprovalClick = () => {
        if (!this.state.previewId) return
        this.setState({isApprovalModalOpen: true})
    }

    closeApprovalModal = () => {
        this.setState({isApprovalModalOpen: false})
    }

    /**
     * The preview modal stays open so the Approval? button visibly becomes
     * "Pending"; the list is refreshed so the card badge appears too.
     */
    handleApprovalSent = (approval: LabelApprovalData) => {
        this.setState({previewApproval: approval})
        this.props.paginate()
    }

    /**
     * Re-emails an outstanding request when the first message never arrived.
     * The approval record is unchanged; the approver gets a fresh link.
     */
    handleResendClick = () => {
        const approval = this.state.previewApproval
        if (!approval || this.state.isResending) return
        this.setState({isResending: true})
        labelApprovalApi.resend(approval.id, updated => {
            this.setState({isResending: false})
            if (!updated) return
            alertService.success(i18n('Approval request sent'))
            this.setState({previewApproval: updated})
            this.props.paginate()
        })
    }

    handleApprovalHistoryClick = (id: number) => {
        const row = this.props.data.find((label: {id: number}) => label.id === id)
        labelApprovalApi.history(this.labelableType, id, approvals => {
            if (!approvals) return
            this.setState({
                approvalHistory: approvals,
                approvalHistoryName: row?.name ?? '',
                isApprovalHistoryOpen: true
            })
        })
    }

    closeApprovalHistory = () => {
        this.setState({isApprovalHistoryOpen: false, approvalHistory: []})
    }

    handleActionClick = (id: number, action: ActionName, additionalInfo?: string) => {
        console.log("additionalInfo", additionalInfo)
        if (action === 'view') {
            const pathname = routes.systemLabelView
            const state = {id}
            this.props.history.push({pathname, state})
        } else if (action === 'list') {
            const pathname = routes.systemLabelPrintsPaginate
            const state = {id}
            this.props.history.push({pathname, state})
        } else if (action === 'print') {
            const pathname = routes.systemLabelPrint
            const state = {id}
            this.props.history.push({pathname, state})
        } else if (action === 'design') {
            const pathname = routes.labelBuilder
            const labelKey = this.state.isCustomLabel
                ? 'customLabelId'
                : 'systemLabelId'
            const state = {[labelKey]: id, labelTarget: additionalInfo}
            this.props.history.push({pathname, state})
        } else if (action === 'preview') {
            const row = this.props.data.find((label: {id: number}) => label.id === id)
            customLabelApi.preview(id, async (markup) => {
                if (!markup) return
                this.setState({
                    previewId: id,
                    previewName: row?.name ?? '',
                    previewApproval: row?.approval ?? null,
                    previewCanApprove: true,
                    previewStage: markup?.stage
                        ? {
                            width: markup.stage.width,
                            height: markup.stage.height,
                            unit: markup.stage.unit
                        }
                        : null
                })
                this.handleImageClick(markup?.snapshot)
            })
        } else if (action === 'edit') {
            this.setState({
                isCreateModalOpen: true,
                isEditId: id
            })
        } else if (action === 'customfield') {
            this.handleGetCustomField(id)
        } else if (action === 'versions') {
            const row = this.props.data.find((label: {id: number}) => label.id === id)
            this.setState({versionsForId: id, versionsName: row?.name ?? ''})
        } else if (action === 'approvals') {
            this.handleApprovalHistoryClick(id)
        } else if (action === 'delete') {
            const confirm: ConfirmParams = {
                id,
                type: 'warning',
                question: i18n('Are you sure to delete the custom label?'),
                confirmText: i18n('Yes'),
                cancelText: i18n('No'),
                onConfirm: () => {
                    this.props.deleteCustomLabel(id);
                    this.props.paginate();
                }
            }
            confirmService.push(confirm)
        }
    }

    componentDidMount() {
        const savedType = this.defaultRadioOption
        // this.props.replaceLabelableType(savedType)
        this.setState({
            isCustomLabel: savedType === 'customLabel'
        })

        storageService.set('systemLabelSearch', savedType)
        storageService.set('customLabelPage', '')
        storageService.set('customLabelLimit', '')
        storageService.set('customLabelSorting', 'desc')
        this.props.paginate()
    }

    renderCalculatedFieldEditor() {
        const index = this.state.editingCalculatedFieldIndex
        if (index === null) return null

        const row = this.state.customFields[index]
        const calculated = row?.calculated
        if (!row || !calculated) return null

        const inputClass = 'input-default'
        const fieldOptions = this.availableCalculatedFields
        const fallback = calculated.fallback ?? {type: 'original' as const}

        return (
            <Modal
                title={`Calculated Field: ${row.name}`}
                isOpen={true}
                onClose={() => this.setState({editingCalculatedFieldIndex: null})}
                className="max-w-5xl"
            >
                <Card>
                    <div className="space-y-4">
                        <div className="rounded border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
                            Rules are evaluated against the original incoming API values. The resolved value replaces <strong>{`api:${row.name}`}</strong> in previews, prints, exports, and reprints.
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Calculation type</label>
                            <select
                                className={inputClass}
                                value={calculated.type}
                                onChange={event => this.handleCalculatedTypeChange(
                                    event.target.value as 'conditional' | 'calculation'
                                )}
                            >
                                <option value="conditional">IF / THEN conditions</option>
                                <option value="calculation">Mathematical calculation</option>
                            </select>
                        </div>

                        {calculated.type === 'conditional' && (
                            <div className="space-y-3">
                                {(calculated.rules ?? []).map((rule, ruleIndex) => (
                                    <div key={ruleIndex} className="rounded border border-gray-200 p-4">
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="font-medium text-gray-900">
                                                {ruleIndex === 0 ? 'IF' : 'ELSE IF'} rule {ruleIndex + 1}
                                            </div>
                                            {(calculated.rules?.length ?? 0) > 1 && (
                                                <button
                                                    type="button"
                                                    className="text-sm text-red-700 hover:text-red-900"
                                                    onClick={() => this.handleRemoveConditionalRule(ruleIndex)}
                                                >
                                                    Remove rule
                                                </button>
                                            )}
                                        </div>

                                        {rule.conditions.length > 1 && (
                                            <div className="mb-3 w-40">
                                                <label className="block text-xs font-medium text-gray-600 mb-1">Match conditions using</label>
                                                <select
                                                    className={inputClass}
                                                    value={rule.combinator}
                                                    onChange={event => this.handleConditionalRuleChange(
                                                        ruleIndex,
                                                        current => ({
                                                            ...current,
                                                            combinator: event.target.value as 'and' | 'or'
                                                        })
                                                    )}
                                                >
                                                    <option value="and">AND</option>
                                                    <option value="or">OR</option>
                                                </select>
                                            </div>
                                        )}

                                        <div className="space-y-2">
                                            {rule.conditions.map((condition, conditionIndex) => (
                                                <div
                                                    key={conditionIndex}
                                                    className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_40px] gap-2 items-end"
                                                >
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-600 mb-1">API field</label>
                                                        <select
                                                            className={inputClass}
                                                            value={condition.field}
                                                            onChange={event => this.handleConditionChange(
                                                                ruleIndex,
                                                                conditionIndex,
                                                                {field: event.target.value}
                                                            )}
                                                        >
                                                            {fieldOptions.map(field => (
                                                                <option key={field} value={field}>{field}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-600 mb-1">Operator</label>
                                                        <select
                                                            className={inputClass}
                                                            value={condition.operator}
                                                            onChange={event => this.handleConditionChange(
                                                                ruleIndex,
                                                                conditionIndex,
                                                                {operator: event.target.value as ApiConditionOperator}
                                                            )}
                                                        >
                                                            {this.conditionOperatorOptions.map(option => (
                                                                <option key={option.value} value={option.value}>{option.label}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-600 mb-1">Compare value</label>
                                                        <input
                                                            className={inputClass}
                                                            value={this.conditionNeedsValue(condition.operator)
                                                                ? String(condition.value ?? '')
                                                                : ''}
                                                            disabled={!this.conditionNeedsValue(condition.operator)}
                                                            onChange={event => this.handleConditionChange(
                                                                ruleIndex,
                                                                conditionIndex,
                                                                {value: event.target.value}
                                                            )}
                                                        />
                                                    </div>
                                                    <button
                                                        type="button"
                                                        aria-label="Remove condition"
                                                        className="h-10 text-gray-500 disabled:opacity-30"
                                                        disabled={rule.conditions.length === 1}
                                                        onClick={() => this.handleRemoveCondition(ruleIndex, conditionIndex)}
                                                    >
                                                        ✕
                                                    </button>
                                                </div>
                                            ))}
                                        </div>

                                        {rule.conditions.length < 10 && (
                                            <button
                                                type="button"
                                                className="mt-3 text-sm text-primary hover:underline"
                                                onClick={() => this.handleAddCondition(ruleIndex)}
                                            >
                                                + Add condition
                                            </button>
                                        )}

                                        <div className="mt-4">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">THEN use this value</label>
                                            <input
                                                className={inputClass}
                                                value={String(rule.result.value ?? '')}
                                                onChange={event => this.handleConditionalRuleChange(
                                                    ruleIndex,
                                                    current => ({
                                                        ...current,
                                                        result: {type: 'literal', value: event.target.value}
                                                    })
                                                )}
                                            />
                                        </div>
                                    </div>
                                ))}

                                {(calculated.rules?.length ?? 0) < 20 && (
                                    <Button
                                        text="Add ELSE IF Rule"
                                        type="secondary"
                                        onClick={this.handleAddConditionalRule}
                                    />
                                )}
                            </div>
                        )}

                        {calculated.type === 'calculation' && calculated.calculation && (
                            <div className="rounded border border-gray-200 p-4 space-y-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Operation</label>
                                    <select
                                        className={inputClass}
                                        value={calculated.calculation.operator}
                                        onChange={event => this.updateCalculatedField(current => ({
                                            ...current,
                                            calculation: {
                                                ...current.calculation!,
                                                operator: event.target.value as 'add' | 'subtract' | 'multiply' | 'divide'
                                            }
                                        }))}
                                    >
                                        <option value="add">Add (+)</option>
                                        <option value="subtract">Subtract (-)</option>
                                        <option value="multiply">Multiply (×)</option>
                                        <option value="divide">Divide (÷)</option>
                                    </select>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    {calculated.calculation.operands.map((operand, operandIndex) => (
                                        <div key={operandIndex} className="rounded bg-gray-50 p-3 space-y-2">
                                            <div className="text-sm font-medium text-gray-700">Operand {operandIndex + 1}</div>
                                            <select
                                                className={inputClass}
                                                value={operand.type}
                                                onChange={event => this.updateCalculatedField(current => {
                                                    const operands = [...current.calculation!.operands] as [ApiCalculationOperand, ApiCalculationOperand]
                                                    operands[operandIndex] = event.target.value === 'field'
                                                        ? {type: 'field', value: fieldOptions[0] ?? ''}
                                                        : {type: 'number', value: 0}
                                                    return {
                                                        ...current,
                                                        calculation: {...current.calculation!, operands}
                                                    }
                                                })}
                                            >
                                                <option value="field">API field</option>
                                                <option value="number">Fixed number</option>
                                            </select>

                                            {operand.type === 'field' ? (
                                                <select
                                                    className={inputClass}
                                                    value={String(operand.value)}
                                                    onChange={event => this.updateCalculatedField(current => {
                                                        const operands = [...current.calculation!.operands] as [ApiCalculationOperand, ApiCalculationOperand]
                                                        operands[operandIndex] = {type: 'field', value: event.target.value}
                                                        return {
                                                            ...current,
                                                            calculation: {...current.calculation!, operands}
                                                        }
                                                    })}
                                                >
                                                    {fieldOptions.map(field => (
                                                        <option key={field} value={field}>{field}</option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <input
                                                    className={inputClass}
                                                    type="number"
                                                    value={String(operand.value)}
                                                    onChange={event => this.updateCalculatedField(current => {
                                                        const operands = [...current.calculation!.operands] as [ApiCalculationOperand, ApiCalculationOperand]
                                                        operands[operandIndex] = {type: 'number', value: event.target.value}
                                                        return {
                                                            ...current,
                                                            calculation: {...current.calculation!, operands}
                                                        }
                                                    })}
                                                />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="rounded border border-gray-200 p-4">
                            <div className="text-sm font-medium text-gray-900">
                                Fallback behavior
                            </div>
                            <p className="mt-1 mb-3 text-sm text-gray-600">
                                Choose what value should be used when no condition matches or a calculation cannot be completed.
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-start">
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                        Fallback option
                                    </label>
                                <select
                                    className={inputClass}
                                    value={fallback.type}
                                    onChange={event => this.updateCalculatedField(current => ({
                                        ...current,
                                        fallback: event.target.value === 'literal'
                                            ? {type: 'literal', value: ''}
                                            : {type: 'original'}
                                    }))}
                                >
                                    <option value="original">Keep original API value</option>
                                    <option value="literal">Use a fixed value</option>
                                </select>
                                </div>

                                {fallback.type === 'literal' ? (
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                            Fixed fallback value <span className="text-red-600">*</span>
                                        </label>
                                        <input
                                            className={`${inputClass} border-blue-400 focus:border-primary`}
                                            value={String(fallback.value ?? '')}
                                            required
                                            aria-required="true"
                                            placeholder="Enter the value to use"
                                            onChange={event => this.updateCalculatedField(current => ({
                                                ...current,
                                                fallback: {type: 'literal', value: event.target.value}
                                            }))}
                                        />
                                        <p className="mt-1 text-xs text-gray-600">
                                            This exact value will replace the calculated field when the fallback is used.
                                        </p>
                                        {String(fallback.value ?? '').trim() === '' && (
                                            <p className="mt-1 text-xs font-medium text-red-600">
                                                Fixed fallback value is required.
                                            </p>
                                        )}
                                    </div>
                                ) : (
                                    <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600">
                                        The original incoming API value will be kept. If the field was not provided, it will remain blank.
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex justify-end">
                            <Button
                                text="Done"
                                type="primary"
                                onClick={this.handleCalculatedEditorDone}
                            />
                        </div>
                    </div>
                </Card>
            </Modal>
        )
    }

    render() {
        const {isLabelBuilderUser} = useUserPlan();
        return (
            <>
                <ContentLayout>
                    <ContentMain layout="full">
                        <Card>
                            <HeadingRow
                                heading={
                                    isLabelBuilderUser
                                        ? i18n('Custom Label Gallery')
                                        : i18n('System Labels')
                                }
                            >
                                <Button
                                    text={i18n('Create')}
                                    onClick={this.handleCreateClick}/>
                            </HeadingRow>
                            <Gallery
                                showSearch={true}
                                limitActions={!this.state.isCustomLabel}
                                useRadios={true}
                                radioOptions={this.searchOptions}
                                defaultRadioOption={this.defaultRadioOption}
                                radioChange={this.handleRadioChange}
                                headers={this.headers}
                                data={this.props.data}
                                total={this.props.total}
                                from={this.props.from}
                                to={this.props.to}
                                currentPage={this.props.currentPage}
                                lastPage={this.props.lastPage}
                                limit={String(this.props.limit)}
                                search={this.props.search}
                                onLimtChange={this.handleLimitChange}
                                onSearch={this.handleSearch}
                                onPaginate={this.handlePaginate}
                                onActionClick={this.handleActionClick}
                                onSortingChange={this.handleSorting}
                                onImagePreview={this.handleImagePreview}
                            />
                        </Card>
                    </ContentMain>
                </ContentLayout>
                <ImagePopup isOpen={this.state.isPopUpOpen} imageLink={this.state.selectedImage}
                            handleCloseClick={this.handlePopupClose}
                            onApproval={this.state.previewId && this.state.previewCanApprove ? this.handleApprovalClick : undefined}
                            onResend={this.handleResendClick}
                            isResending={this.state.isResending}
                            approvalStatus={this.state.previewApproval?.status ?? null}/>

                {this.state.isApprovalModalOpen && this.state.previewId && (
                    <LabelApprovalRequestForm
                        isOpen={this.state.isApprovalModalOpen}
                        labelableType={this.labelableType}
                        labelableId={this.state.previewId}
                        labelName={this.state.previewName}
                        snapshotUrl={this.state.selectedImage}
                        labelWidth={this.state.previewStage?.width ?? this.state.previewApproval?.labelWidth}
                        labelHeight={this.state.previewStage?.height ?? this.state.previewApproval?.labelHeight}
                        labelUnit={this.state.previewStage?.unit ?? this.state.previewApproval?.labelUnit}
                        defaultApproverName={this.state.previewApproval?.approverName}
                        defaultApproverEmail={this.state.previewApproval?.approverEmail}
                        onClose={this.closeApprovalModal}
                        onSent={this.handleApprovalSent}/>
                )}

                {this.state.versionsForId && (
                    <LabelVersionHistory
                        isOpen={true}
                        customLabelId={this.state.versionsForId}
                        labelName={this.state.versionsName}
                        onClose={() => this.setState({versionsForId: null, versionsName: ''})}
                        onRestored={() => this.props.paginate()}/>
                )}

                {this.state.isApprovalHistoryOpen && (
                    <LabelApprovalHistory
                        isOpen={this.state.isApprovalHistoryOpen}
                        labelName={this.state.approvalHistoryName}
                        approvals={this.state.approvalHistory}
                        onClose={this.closeApprovalHistory}/>
                )}

                {this.state.isCreateModalOpen && (
                    this.state.isEditId !== null ? (
                        <CustomLabelEditForm
                            isCreateModalOpen={this.state.isCreateModalOpen}
                            setIsCreateModalOpen={this.closeCreateModal}
                            onSuccess={() => this.props.paginate()}
                            editId={this.state.isEditId}
                        />
                    ) : (
                        <CustomLabelCreateForm
                            isCreateModalOpen={this.state.isCreateModalOpen}
                            setIsCreateModalOpen={this.closeCreateModal}
                            onSuccess={() => this.props.paginate()}
                        />
                    )
                )}


                <Modal
                    title={i18n("Custom Fields")}
                    isOpen={this.state.isCustomFieldModalOpen}
                        onClose={this.closeCustomFieldModal}
                    className={'max-w-4xl'}
                >
                    <Card>
                        <div className="flex flex-col">
                            <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_120px_140px_40px] gap-3 items-center mb-2">
                                <div className="text-sm font-medium text-gray-700">
                                    {i18n('Custom Field')}
                                </div>
                                <div className="text-sm font-medium text-gray-700">
                                    {i18n('Value')}
                                </div>
                                <div className="flex items-center gap-1 text-sm font-medium text-gray-700">
                                    <span>Calculated?</span>
                                    <Tooltip
                                        text="Transform this field with IF/THEN rules or calculate it from numeric API fields."
                                        maxWidth={300}
                                    >
                                        <span className="flex items-center">
                                            <Icon name="question" size="small" color="gray" />
                                        </span>
                                    </Tooltip>
                                </div>
                                <div className="flex items-center gap-1 text-sm font-medium text-gray-700">
                                    <span>PII or GDPR?</span>
                                    <Tooltip
                                        text="Checked fields will still be used on the label however, their live values will not be retained within our internal databases or any secondary storage systems."
                                        maxWidth={320}
                                    >
                                        <span className="flex items-center">
                                            <Icon
                                                name="question"
                                                size="small"
                                                color="gray"
                                            />
                                        </span>
                                    </Tooltip>
                                </div>
                                <div />
                            </div>

                            {this.state.customFields.map((field, index) => (
                                <div key={index} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_120px_140px_40px] gap-3 items-start">

                                    <InputText
                                        name={`customField_${index}`}
                                        label={`${i18n("Custom Field")} ${index + 1}`}
                                        value={field?.name ?? ''}
                                        readonly={index === 0}
                                        onChange={(_, value) =>
                                            this.handleCustomFieldChange(index, 'name', value)
                                        }
                                    />

                                    <InputText
                                        name={`sampleField_${index}`}
                                        label={i18n("Value")}
                                        value={field?.sample ?? ''}
                                        onChange={(_, value) =>
                                            this.handleCustomFieldChange(index, 'sample', value)
                                        }
                                    />

                                    <div className="flex items-center gap-2 mt-8 h-6">
                                        <input
                                            type="checkbox"
                                            checked={field.calculated !== null}
                                            disabled={index === 0 || field.name.trim() === ''}
                                            onChange={event => this.handleCalculatedToggle(index, event.target.checked)}
                                        />
                                        {field.calculated && (
                                            <Icon
                                                name="edit"
                                                size="small"
                                                color="gray"
                                                onClick={() => this.setState({editingCalculatedFieldIndex: index})}
                                            />
                                        )}
                                    </div>

                                    <label className="flex items-center gap-2 mt-8 text-sm text-gray-700 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={field?.isPii ?? false}
                                            onChange={(event) =>
                                                this.handleCustomFieldChange(index, 'isPii', event.target.checked)
                                            }
                                        />
                                    </label>

                                    {index !== 0 && (
                                        <div className="mt-8">
                                            <Icon
                                                name={"delete-bin-6"}
                                                size="medium"
                                                color="gray"
                                                onClick={() => this.handleCustomFieldDelete(index)}
                                            />
                                        </div>
                                    )}
                                </div>
                            ))}

                            <div className={`flex py-3 ${this.state.isExampleVisible ? 'justify-end' : 'justify-between'}`}>
                                {
                                    !this.state.isExampleVisible &&
                                    <Button
                                        text={'JSON API Code Snippet'}
                                        onClick={this.handleExampleVisibility}
                                        type="secondary"
                                    />
                                }
                                {this.state.customFields.length < 30 && (
                                    <Button
                                        text={i18n('Add Custom Field')}
                                        onClick={this.handleAddCustomField}
                                        type="primary"
                                    />
                                )}
                            </div>

                            <div className="mt-4 border-t pt-4">
                                <div className="flex items-center justify-between gap-3 flex-wrap">
                                    <div>
                                        <h4 className="text-base font-medium text-gray-900">
                                            API Request Example
                                        </h4>
                                        <p className="text-sm text-gray-600">
                                            Choose a method to see how customers should send these sample keys and values.
                                        </p>
                                    </div>

                                    {
                                        this.state.isExampleVisible &&
                                            <Button
                                                text={"Hide"}
                                                onClick={this.handleExampleVisibility}
                                                type="secondary"
                                            />
                                    }
                                </div>

                                {this.state.isExampleVisible && (
                                    <div className="mt-4">
                                        <div className="flex items-center justify-end">
                                            <Button
                                                text={'Copy Example'}
                                                onClick={this.handleCopyExample}
                                                type="secondary"
                                            />
                                        </div>

                                        <div className="flex flex-wrap gap-2 mt-3">
                                            {this.methodOptions.map(option => {
                                                const isActive = option.value === this.state.selectedApiMethod
                                                return (
                                                    <button
                                                        key={option.value}
                                                        type="button"
                                                        className={`px-3 py-2 text-sm border ${isActive ? 'bg-primary text-white border-primary' : 'bg-white text-gray-700 border-gray-300'}`}
                                                        onClick={() => this.handleMethodChange(option.value)}
                                                    >
                                                        {option.label}
                                                    </button>
                                                )
                                            })}
                                        </div>

                                        <div className="mt-4 rounded border border-gray-200 bg-gray-50 p-4">
                                            <div className="text-sm font-medium text-gray-900">
                                                {this.selectedMethodMeta.title}
                                            </div>
                                            <p className="text-sm text-gray-600 mt-1">
                                                {this.selectedMethodMeta.note}
                                            </p>
                                            <p className="text-sm text-red-800 mt-2">
                                                Replace these example/sample values with the real or dynamic values from your customer system before using the request in production.
                                            </p>

                                            <div className="mt-3">
                                                <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">
                                                    Sample data used
                                                </div>
                                                <pre className="overflow-x-auto bg-white border border-gray-200 p-3 text-xs text-gray-800">
{JSON.stringify(this.requestBodyObject, null, 2)}
                                                </pre>
                                            </div>

                                            <div className="mt-3">
                                                <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">
                                                    Example call
                                                </div>
                                                <pre className="overflow-x-auto bg-[#0f172a] p-4 text-xs text-white whitespace-pre-wrap">
{this.selectedMethodMeta.code}
                                                </pre>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                        </div>

                        <div className="flex justify-end gap-3 mt-3">
                            <Button
                                text={i18n('Cancel')}
                                onClick={this.closeCustomFieldModal}
                                type="secondary"
                            />

                            <Button
                                text={i18n('Save')}
                                onClick={this.handleSaveCustomFields}
                                type="primary"
                            />
                        </div>
                    </Card>
                </Modal>

                {this.renderCalculatedFieldEditor()}


            </>
        )
    }
}

const mapStateToProps = (state: RootState) => {
    const {result, limit, search, sortBy} = state.systemLabels
    const {data, total, from, to, currentPage, lastPage} = result
    return {data, total, from, to, currentPage, lastPage, limit, search, sortBy}
}

const mapDispatchToProps = {
    paginate,
    deleteCustomLabel,
    replacePage,
    replaceLimit,
    replaceSearch,
    replaceLabelableType,
    replaceSorting
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(withRouter(SystemLabels))
